import React from "react";
import SEO from "../components/SEO";

export default function Features() {
  return (
    <main className="page">
      <SEO
        title="BossDrop | Features"
        description="BossDrop does the management work: tasks, meetings, escalations, exec reports, meeting notes, an email and chat assistant, custom automations and team memory."
        canonical="https://bossdrop.ai/features"
        url="https://bossdrop.ai/features"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "BossDrop",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          url: "https://bossdrop.ai/",
          publisher: {
            "@type": "Organization",
            name: "BossDrop",
            url: "https://bossdrop.ai/",
          },
        }}
        breadcrumbsJsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://bossdrop.ai/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Features",
              item: "https://bossdrop.ai/features",
            },
          ],
        }}
      />

      <section className="hero">
        <h1 className="h1">Features</h1>
        <p className="sub">
          BossDrop plans, assigns, schedules, escalates, and reports,
          automatically, and learns how your team runs as it goes. It does the
          work a manager would do, 24/7.
        </p>
      </section>

      <section className="grid cards">
        {[
          {
            icon: "tasks",
            title: "Auto-Created Tasks",
            body: "Open and update tasks in Asana, Jira, Linear, ClickUp, Notion, monday.com or Planner with owners, deadlines, and measurable targets.",
          },
          {
            icon: "calendar",
            title: "Auto-Scheduled Huddles",
            body: "Create Google/Outlook holds with agenda + context for quick decisions.",
          },
          {
            icon: "escalate",
            title: "Escalations",
            body: "Tell the right person on Slack, Teams, or email what’s blocked, by when, and budget/impact context.",
          },
          {
            icon: "risk",
            title: "Risk Detection",
            body: "Spot drift and risk language across threads, tasks, and updates, early.",
          },
          {
            icon: "updates",
            title: "Daily Task Updates",
            body: "DM task owners to get status updates on tasks then proactively fix blockers.",
          },
          {
            icon: "report",
            title: "Weekly Exec Report",
            body: "KPIs, risks, and approvals, clear and skimmable. Auto-sends at the time that works best for you.",
          },
          {
            icon: "chat",
            title: "Email and Chat Assistant",
            body: "Message BossDrop in Slack or Teams, or email ai@bossdrop.ai, to ask what’s going on or get something done. It answers like a manager would and keeps the thread in context.",
          },
          {
            icon: "notes",
            title: "Meeting Notes",
            body: "Turn it on and BossDrop reads the Teams or Google Meet transcript after meetings it booked, then sends notes and follow-up tasks. It never joins or records the call, and any invitee can opt out.",
          },
          {
            icon: "automation",
            title: "Custom Automations",
            body: "Describe a workflow in your own words. BossDrop works out when it should run and what it should do, then runs it and checks the result.",
          },
        ].map((f, i) => (
          <article className="card" key={i}>
            <span
              aria-hidden="true"
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg, #8fd8f0, #8f7fd0)",
                boxShadow: "0 6px 16px rgba(143,216,240,.25)",
                marginBottom: 12,
              }}
            >
              {featureIcon(f.icon)}
            </span>
            <h3>{f.title}</h3>
            <p>{f.body}</p>
          </article>
        ))}
      </section>

      <section className="panel panelDark">
        <h2 className="h2">Team Memory: it learns how your team runs</h2>
        <p>
          BossDrop keeps one structured document of how your team works: owners,
          cadence, handoffs, policies, and thresholds. It rewrites that document
          every day from real work, and it changes who BossDrop asks, when it
          checks in, and how long it waits before going to a manager.
        </p>
        <ul className="bullets">
          <li>Every decision records which lines it used, and the Memory page shows them.</li>
          <li>A workspace admin can correct any line in their own words, and that outranks what BossDrop measured.</li>
          <li>Admins can export all of it, as plain text or JSON, at any time.</li>
          <li>It never ranks people.</li>
        </ul>
      </section>

      <section className="ctaBand">
        <div className="ctaInner">
          <h2 className="h2">Stop managing status. Start shipping outcomes.</h2>
          <p className="sub">
            Connect Slack or Microsoft Teams, your task tool (Asana, Jira, Linear, ClickUp, Notion, monday.com or Planner), and Google or Outlook. We’ll take it from there.
          </p>
          <a className="btn btnPrimary" href="/pricing">
            See pricing
          </a>
        </div>
      </section>
    </main>
  );
}

/* Little inline icons for the feature cards (dark stroke on the gradient badge). */
function featureIcon(name) {
  const p = {
    width: 22,
    height: 22,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "#121020",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  switch (name) {
    case "tasks":
      return (
        <svg {...p}>
          <rect x="3" y="4" width="18" height="16" rx="3" />
          <path d="M8 12l3 3 5-6" />
        </svg>
      );
    case "calendar":
      return (
        <svg {...p}>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 9h18M8 3v4M16 3v4" />
        </svg>
      );
    case "escalate":
      return (
        <svg {...p}>
          <path d="M6 9a6 6 0 0112 0c0 5 2 6 2 6H4s2-1 2-6z" />
          <path d="M10 21a2 2 0 004 0" />
        </svg>
      );
    case "risk":
      return (
        <svg {...p}>
          <path d="M12 3l9 16H3z" />
          <path d="M12 10v4M12 17h.01" />
        </svg>
      );
    case "updates":
      return (
        <svg {...p}>
          <path d="M3 12a9 9 0 0115-6.7L21 8" />
          <path d="M21 3v5h-5" />
          <path d="M21 12a9 9 0 01-15 6.7L3 16" />
          <path d="M3 21v-5h5" />
        </svg>
      );
    case "report":
      return (
        <svg {...p}>
          <rect x="4" y="3" width="16" height="18" rx="2" />
          <path d="M8 8h8M8 12h8M8 16h5" />
        </svg>
      );
    case "chat":
      return (
        <svg {...p}>
          <path d="M4 5h16a1 1 0 011 1v10a1 1 0 01-1 1H9l-5 4V6a1 1 0 011-1z" />
          <path d="M8 10h8M8 13h5" />
        </svg>
      );
    case "notes":
      return (
        <svg {...p}>
          <path d="M6 3h9l4 4v14H6z" />
          <path d="M15 3v4h4" />
          <path d="M9 12h6M9 16h4" />
        </svg>
      );
    case "automation":
      return (
        <svg {...p}>
          <path d="M13 2L4 14h7l-1 8 9-12h-7z" />
        </svg>
      );
    default:
      return null;
  }
}
